import styled from "styled-components";
import { MainContent } from "../../styles/shared/drawer";
import { WhatsappMessage } from "../../services/organisation/whatsapp-communications-service";

type DeliveryTickState = "pending" | "sent" | "delivered" | "read" | "failed";

interface MessageStatusHistoryDrawerProps {
  message?: WhatsappMessage;
  outgoing?: boolean;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat([], {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getLatestStatus(message: WhatsappMessage) {
  const history = [...(message.statusHistory ?? [])].sort((left, right) => {
    const leftTime = new Date(left.timestamp ?? 0).getTime();
    const rightTime = new Date(right.timestamp ?? 0).getTime();
    return rightTime - leftTime;
  });

  return message.currentStatus ?? history[0]?.status ?? "";
}

function getFailureMessage(message: WhatsappMessage) {
  if (message.errorMessage) {
    return message.errorMessage;
  }

  const latestFailure = [...(message.statusHistory ?? [])]
    .sort((left, right) => {
      const leftTime = new Date(left.timestamp ?? 0).getTime();
      const rightTime = new Date(right.timestamp ?? 0).getTime();
      return rightTime - leftTime;
    })
    .find((status) => {
      const value = (status.status ?? "").toLowerCase();
      return value.includes("fail") || value.includes("error") || value.includes("undeliver");
    });

  return latestFailure?.errorMessage ?? null;
}

function getDeliveryTickState(message: WhatsappMessage): DeliveryTickState {
  const statusValue = getLatestStatus(message).toLowerCase();

  if (!statusValue) {
    return "pending";
  }

  if (
    statusValue.includes("fail") ||
    statusValue.includes("error") ||
    statusValue.includes("undeliver") ||
    statusValue.includes("rejected")
  ) {
    return "failed";
  }

  if (statusValue.includes("read") || statusValue.includes("seen")) {
    return "read";
  }

  if (statusValue.includes("deliver")) {
    return "delivered";
  }

  if (statusValue.includes("sent") || statusValue.includes("submit") || statusValue.includes("accepted")) {
    return "sent";
  }

  return "pending";
}

function TickIndicator({ state }: { state: DeliveryTickState }) {
  if (state === "pending") {
    return <TickText $state={state}>clock</TickText>;
  }

  if (state === "sent") {
    return <TickText $state={state}>check</TickText>;
  }

  if (state === "failed") {
    return <TickText $state={state}>priority_high</TickText>;
  }

  return <TickText $state={state}>done_all</TickText>;
}

export default function MessageStatusHistoryDrawer({
  message,
  outgoing,
}: MessageStatusHistoryDrawerProps) {
  if (!message) {
    return (
      <MainContent>
        <EmptyText>No message selected.</EmptyText>
      </MainContent>
    );
  }

  const statusHistory = [...(message.statusHistory ?? [])].sort((left, right) => {
    const leftTime = new Date(left.timestamp ?? 0).getTime();
    const rightTime = new Date(right.timestamp ?? 0).getTime();
    return rightTime - leftTime;
  });

  const failureMessage = getFailureMessage(message);
  const latestStatus = getLatestStatus(message);
  const tickState = getDeliveryTickState(message);

  return (
    <MainContent>
      <Wrapper>
        <MessageCard>
          <MessageLabel>{outgoing ? "Outgoing message" : "Incoming message"}</MessageLabel>
          <MessageContent>{message.content || message.caption || "Media message"}</MessageContent>
          {failureMessage ? <FailureText>{failureMessage}</FailureText> : null}

          <MetaRow>
            <StatusPill>
              <TickIndicator state={tickState} />
              <StatusText>{latestStatus || "Pending"}</StatusText>
            </StatusPill>
            <MetaTime>{formatDateTime(message.timestamp)}</MetaTime>
          </MetaRow>
        </MessageCard>

        <SectionTitle>Status History</SectionTitle>
        {statusHistory.length === 0 ? (
          <EmptyText>No status history available for this message yet.</EmptyText>
        ) : (
          <HistoryList>
            {statusHistory.map((status) => {
              const statusValue = status.status ?? "Unknown";
              const statusIsError = /fail|error|undeliver|rejected/i.test(statusValue);

              return (
                <HistoryItem key={status.id ?? `${status.status}-${status.timestamp}`}>
                  <HistoryTop>
                    <HistoryStatus $error={statusIsError}>{statusValue}</HistoryStatus>
                    <HistoryTime>{formatDateTime(status.timestamp)}</HistoryTime>
                  </HistoryTop>
                  {status.errorMessage ? (
                    <HistoryError>{status.errorMessage}</HistoryError>
                  ) : null}
                  {status.errorCode ? (
                    <HistoryCode>Error code: {status.errorCode}</HistoryCode>
                  ) : null}
                </HistoryItem>
              );
            })}
          </HistoryList>
        )}
      </Wrapper>
    </MainContent>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageCard = styled.div`
  border: 1px solid rgba(7, 94, 84, 0.18);
  border-radius: 14px;
  padding: 14px;
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(7, 94, 84, 0.06);
`;

const MessageLabel = styled.div`
  font-size: 0.8rem;
  color: #667781;
  margin-bottom: 6px;
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  color: #10211f;
  line-height: 1.5;
`;

const FailureText = styled.div`
  margin-top: 10px;
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 0.86rem;
`;

const MetaRow = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f4f7f8;
`;

const StatusText = styled.div`
  font-size: 0.82rem;
  color: #0b3f38;
  text-transform: capitalize;
`;

const MetaTime = styled.div`
  font-size: 0.8rem;
  color: #667781;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: #0b3f38;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HistoryItem = styled.div`
  border: 1px solid rgba(7, 94, 84, 0.14);
  border-radius: 12px;
  padding: 10px;
  background: #ffffff;
`;

const HistoryTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const HistoryStatus = styled.div<{ $error: boolean }>`
  color: ${({ $error }) => ($error ? "#b42318" : "#0b3f38")};
  font-weight: 700;
  text-transform: capitalize;
`;

const HistoryTime = styled.div`
  color: #667781;
  font-size: 0.8rem;
`;

const HistoryError = styled.div`
  margin-top: 6px;
  font-size: 0.85rem;
  color: #b42318;
`;

const HistoryCode = styled.div`
  margin-top: 4px;
  font-size: 0.8rem;
  color: #667781;
`;

const EmptyText = styled.div`
  color: #667781;
  font-size: 0.9rem;
`;

const TickText = styled.span<{ $state: DeliveryTickState }>`
  font-family: "Material Symbols Outlined";
  font-size: 1rem;
  line-height: 1;
  color: ${({ $state }) => {
    if ($state === "read") {
      return "#128c7e";
    }

    if ($state === "failed") {
      return "#b42318";
    }

    return "#667781";
  }};
`;
